import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Chip, Popover } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { Pressable, View } from 'react-native';
import {
  RejectionReason,
  RejectionReasonTag,
} from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';
import { TextInput } from '@/experiences/sales-portal/components/text-input';
import { Typography } from '@usepepper/ui/atoms/typography';

const NOTE_DEBOUNCE_MS = 300;

const TAGS: { id: RejectionReasonTag; label: string }[] = [
  {
    id: RejectionReasonTag.WrongData,
    label: i18n.t('pricing.agent.review.reason.tag.wrong_data'),
  },
  {
    id: RejectionReasonTag.AtRisk,
    label: i18n.t('pricing.agent.review.reason.tag.at_risk'),
  },
  {
    id: RejectionReasonTag.SpecialDeal,
    label: i18n.t('pricing.agent.review.reason.tag.special_deal'),
  },
];

interface Props {
  initialReason?: RejectionReason;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onReasonChange: (reason: RejectionReason) => void;
  trigger: React.ReactNode;
}

export const AgentReasonPopover: React.FC<Props> = ({
  initialReason,
  isOpen,
  onOpenChange,
  onReasonChange,
  trigger,
}) => {
  const [styles, theme] = useStyles();

  const [selectedTags, setSelectedTags] = useState<RejectionReasonTag[]>(
    initialReason?.tags ?? [],
  );
  const [note, setNote] = useState(initialReason?.note ?? '');

  const wasOpenRef = useRef(isOpen);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setNote(initialReason?.note ?? '');
      setSelectedTags(initialReason?.tags ?? []);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, initialReason]);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPendingNoteCommit = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);
  useEffect(() => clearPendingNoteCommit, [clearPendingNoteCommit]);

  const commitReason = useCallback(
    (nextTags: RejectionReasonTag[], nextNote: string) => {
      const trimmed = nextNote.trim();
      onReasonChange({
        note: trimmed ? trimmed : null,
        tags: nextTags,
      });
    },
    [onReasonChange],
  );

  const toggleTag = useCallback(
    (tagId: RejectionReasonTag) => {
      clearPendingNoteCommit();
      const next = selectedTags.includes(tagId)
        ? selectedTags.filter((t) => t !== tagId)
        : [...selectedTags, tagId];
      setSelectedTags(next);
      commitReason(next, note);
    },
    [clearPendingNoteCommit, commitReason, note, selectedTags],
  );

  const handleNoteChange = useCallback(
    (text: string) => {
      setNote(text);
      clearPendingNoteCommit();
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        commitReason(selectedTags, text);
      }, NOTE_DEBOUNCE_MS);
    },
    [clearPendingNoteCommit, commitReason, selectedTags],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && debounceTimerRef.current) {
        clearPendingNoteCommit();
        commitReason(selectedTags, note);
      }
      onOpenChange(nextOpen);
    },
    [clearPendingNoteCommit, commitReason, note, onOpenChange, selectedTags],
  );

  return (
    <Popover
      allowFlip
      offset={4}
      onOpenChange={handleOpenChange}
      open={isOpen}
      placement="bottom-start"
    >
      <Popover.Anchor>{trigger}</Popover.Anchor>
      <Popover.Content disableFocusScope>
        {/* Portaled content still bubbles presses to the row Pressable; swallow them. */}
        <Pressable
          accessible={false}
          focusable={false}
          style={styles.content}
          testID="agent-reason-content"
        >
          <Typography variant="paragraph-md-600">
            {i18n.t('pricing.agent.review.reason.reject_title')}
          </Typography>
          <View style={styles.tags}>
            {TAGS.map((tag) => {
              const selected = selectedTags.includes(tag.id);
              return (
                <Pressable
                  accessibilityLabel={tag.label}
                  accessibilityRole="checkbox"
                  aria-checked={selected}
                  key={tag.id}
                  onPress={() => toggleTag(tag.id)}
                  testID={`agent-reason-tag-${tag.id}`}
                >
                  <Chip circular pressable selected={selected} size="$md">
                    <Chip.Text>{tag.label}</Chip.Text>
                  </Chip>
                </Pressable>
              );
            })}
          </View>
          <TextInput
            multiline
            onChangeText={handleNoteChange}
            placeholder={i18n.t('pricing.agent.review.reason.note_placeholder')}
            placeholderTextColor={theme.sk.color.text.tertiary}
            style={styles.note}
            testID="agent-reason-note"
            value={note}
          />
        </Pressable>
      </Popover.Content>
    </Popover>
  );
};

const useStyles = createStyles((theme) => ({
  content: {
    gap: theme.sk.spacing.spaceSMedium,
    padding: theme.sk.spacing.spaceMedium,
    width: 280,
  },
  note: {
    fontSize: theme.sk.typography.paragraph.md.fontSize,
  },
  tags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
